import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import { createProject, updateProject, deleteProject, getProjects } from '../api';
import { ProjectForm } from './ProjectForm';

export const ProjectsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [projects, setProjects] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingProject, setEditingProject] = React.useState<any>(null);

  const fetchProjects = React.useCallback(() => {
    setLoading(true);
    getProjects()
      .then(data => { setProjects(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  React.useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async (data: any) => {
    try {
      await createProject(data);
      setDialogOpen(false);
      fetchProjects();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = async (data: any) => {
    try {
      await updateProject(editingProject.id, data);
      setDialogOpen(false);
      setEditingProject(null);
      fetchProjects();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('confirm_delete') || 'Are you sure?')) {
      try {
        await deleteProject(id);
        fetchProjects();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  return (
    <>
      <Box mb={2} display="flex" gap={2} alignItems="center">
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => setDialogOpen(true)}
          fullWidth={isMobile}
        >
          {t('create') || 'Create'}
        </Button>
      </Box>
      {loading ? (
        <Typography>{t('loading') || 'Loading...'}</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {projects.map((project) => (
            <Card key={project.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                    {project.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {project.id}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  ID проекта: {project.project_id}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {project.description || 'Нет описания'}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(project.created_at).toLocaleString('ru-RU')}
                  </Typography>
                  <Box>
                    <IconButton size="small" color="primary" onClick={() => { setEditingProject(project); setDialogOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(project.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>{t('project_id') || 'Project ID'}</TableCell>
                <TableCell>{t('name') || 'Name'}</TableCell>
                <TableCell>{t('description') || 'Description'}</TableCell>
                <TableCell>{t('created_at') || 'Created at'}</TableCell>
                <TableCell>{t('actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => (
                <TableRow key={project.id}>
                  <TableCell>{project.id}</TableCell>
                  <TableCell>{project.project_id}</TableCell>
                  <TableCell>{project.name}</TableCell>
                  <TableCell>{project.description}</TableCell>
                  <TableCell>{project.created_at}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => { setEditingProject(project); setDialogOpen(true); }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(project.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ProjectForm
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingProject(null); }}
        project={editingProject}
        onSubmit={editingProject ? handleEdit : handleCreate}
      />
    </>
  );
}; 